import { useMemo } from "react";
import {
  Combobox,
  FormField,
  MultiSelect,
  TextInput,
  type MultiSelectOption,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import type {
  CouponDiscountUnit,
  CouponKind,
  PromotionScope,
} from "@/lib/api/types";
import { useProductsQuery } from "@/modules/products/services/useProductsQuery";
import { useCategoriesQuery } from "@/shared/services/useCategoriesQuery";
import {
  kindUsesScope,
  kindUsesValue,
  type PromotionFieldErrors,
  type PromotionFieldKeys,
  type PromotionState,
} from "@/modules/coupons/lib/promotion-form";

type PromotionFieldsProps = {
  /** i18n keys for the hosting namespace (coupons or discounts). */
  k: PromotionFieldKeys;
  storeSlug: string;
  locale: Locale;
  value: PromotionState;
  onChange: (patch: Partial<PromotionState>) => void;
  errors: PromotionFieldErrors;
  onClearError: (field: keyof PromotionFieldErrors) => void;
};

const PRODUCT_PICKER_LIMIT = 200;

/**
 * Shared "what does this promotion do" fieldset used by both the coupon and the
 * discount forms: a kind selector plus the conditional value / Buy-X-Get-Y /
 * scope inputs. State lives in the parent form; this component only renders and
 * reports changes back through `onChange`.
 */
export function PromotionFields({
  k,
  storeSlug,
  locale,
  value,
  onChange,
  errors,
  onClearError,
}: PromotionFieldsProps) {
  const t = useTranslations();

  const showScope = kindUsesScope(value.kind);
  const scopeProducts = showScope && value.appliesTo === "PRODUCTS";
  const scopeCategories = showScope && value.appliesTo === "CATEGORIES";

  const { data: productsData, isLoading: productsLoading } = useProductsQuery({
    storeSlug,
    limit: PRODUCT_PICKER_LIMIT,
  });
  const { data: categoriesData, isLoading: categoriesLoading } =
    useCategoriesQuery({ storeSlug });

  const productOptions = useMemo<MultiSelectOption[]>(
    () =>
      (productsData?.items ?? []).map((product) => ({
        value: product.id,
        label: pickLocalized(locale, product.nameAr, product.nameEn),
      })),
    [productsData, locale],
  );

  const categoryOptions = useMemo<MultiSelectOption[]>(
    () =>
      (categoriesData ?? []).map((category) => ({
        value: category.id,
        label: pickLocalized(locale, category.nameAr, category.nameEn),
      })),
    [categoriesData, locale],
  );

  return (
    <>
      {/* Kind */}
      <FormField label={t(k.kindLabel)} name="kind">
        <Combobox
          value={value.kind}
          onChange={(next) => next && onChange({ kind: next as CouponKind })}
          options={[
            { value: "CART_DISCOUNT", label: t(k.kindCartDiscount) },
            { value: "PRODUCT_DISCOUNT", label: t(k.kindProductDiscount) },
            { value: "FREE_DELIVERY", label: t(k.kindFreeDelivery) },
            { value: "BUY_X_GET_Y", label: t(k.kindBogo) },
          ]}
          aria-label={t(k.kindLabel)}
        />
      </FormField>

      {/* Value (cart + product discounts) */}
      {kindUsesValue(value.kind) ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t(k.discountUnitLabel)} name="discountUnit">
            <Combobox
              value={value.discountUnit}
              onChange={(next) =>
                next && onChange({ discountUnit: next as CouponDiscountUnit })
              }
              options={[
                { value: "PERCENT", label: t(k.discountUnitPercent) },
                { value: "FIXED", label: t(k.discountUnitFixed) },
              ]}
              aria-label={t(k.discountUnitLabel)}
            />
          </FormField>
          <FormField
            label={t(k.discountValueLabel)}
            name="discountValue"
            required
            error={errors.discountValue}
          >
            <div className="relative">
              <TextInput
                name="discountValue"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={value.discountValue}
                placeholder={
                  value.discountUnit === "PERCENT"
                    ? t(k.discountValuePercentPlaceholder)
                    : t(k.discountValueFixedPlaceholder)
                }
                required
                hasError={Boolean(errors.discountValue)}
                onChange={(event) => {
                  onChange({ discountValue: event.target.value });
                  onClearError("discountValue");
                }}
                className="pe-10"
              />
              <span
                className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-[12px] font-semibold text-[color:var(--text-muted)]"
                aria-hidden="true"
              >
                {value.discountUnit === "PERCENT" ? "%" : "EGP"}
              </span>
            </div>
          </FormField>
        </div>
      ) : null}

      {/* Buy X get Y */}
      {value.kind === "BUY_X_GET_Y" ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            label={t(k.bogoBuyQtyLabel)}
            name="buyQuantity"
            required
            error={errors.buyQuantity}
          >
            <TextInput
              name="buyQuantity"
              type="number"
              inputMode="numeric"
              step="1"
              min="1"
              value={value.buyQuantity}
              placeholder={t(k.bogoBuyQtyPlaceholder)}
              required
              hasError={Boolean(errors.buyQuantity)}
              onChange={(event) => {
                onChange({ buyQuantity: event.target.value });
                onClearError("buyQuantity");
              }}
            />
          </FormField>
          <FormField
            label={t(k.bogoGetQtyLabel)}
            name="getQuantity"
            required
            error={errors.getQuantity}
          >
            <TextInput
              name="getQuantity"
              type="number"
              inputMode="numeric"
              step="1"
              min="1"
              value={value.getQuantity}
              placeholder={t(k.bogoGetQtyPlaceholder)}
              required
              hasError={Boolean(errors.getQuantity)}
              onChange={(event) => {
                onChange({ getQuantity: event.target.value });
                onClearError("getQuantity");
              }}
            />
          </FormField>
          <FormField
            label={t(k.bogoGetPercentLabel)}
            name="getDiscountPercent"
            required
            error={errors.getDiscountPercent}
            helperText={
              errors.getDiscountPercent ? undefined : t(k.bogoGetPercentHint)
            }
          >
            <div className="relative">
              <TextInput
                name="getDiscountPercent"
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                max="100"
                value={value.getDiscountPercent}
                placeholder={t(k.bogoGetPercentPlaceholder)}
                required
                hasError={Boolean(errors.getDiscountPercent)}
                onChange={(event) => {
                  onChange({ getDiscountPercent: event.target.value });
                  onClearError("getDiscountPercent");
                }}
                className="pe-10"
              />
              <span
                className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-[12px] font-semibold text-[color:var(--text-muted)]"
                aria-hidden="true"
              >
                %
              </span>
            </div>
          </FormField>
        </div>
      ) : null}

      {/* Scope (product + BOGO) */}
      {showScope ? (
        <FormField
          label={t(k.scopeLabel)}
          name="appliesTo"
          error={value.appliesTo === "ALL" ? undefined : errors.scope}
        >
          <div className="flex flex-col gap-3">
            <Combobox
              value={value.appliesTo}
              onChange={(next) => {
                if (!next) return;
                onChange({ appliesTo: next as PromotionScope });
                onClearError("scope");
              }}
              options={[
                { value: "ALL", label: t(k.scopeAll) },
                { value: "PRODUCTS", label: t(k.scopeProducts) },
                { value: "CATEGORIES", label: t(k.scopeCategories) },
              ]}
              aria-label={t(k.scopeLabel)}
            />
            {scopeProducts ? (
              <FormField
                label={t(k.scopeProductsLabel)}
                name="productIds"
                error={errors.scope}
              >
                {productsLoading ? (
                  <p className="text-[12.5px] text-[color:var(--text-muted)]">
                    {t(k.scopeLoading)}
                  </p>
                ) : productOptions.length === 0 ? (
                  <p className="text-[12.5px] text-[color:var(--text-muted)]">
                    {t(k.scopeProductsEmpty)}
                  </p>
                ) : (
                  <MultiSelect
                    options={productOptions}
                    value={value.productIds}
                    onChange={(ids) => {
                      onChange({ productIds: ids });
                      onClearError("scope");
                    }}
                    placeholder={t(k.scopePickerPlaceholder)}
                    searchPlaceholder={t(k.scopeSearchPlaceholder)}
                    noResultsLabel={t(k.scopeNoResults)}
                    hasError={Boolean(errors.scope)}
                    aria-label={t(k.scopeProductsLabel)}
                  />
                )}
              </FormField>
            ) : null}
            {scopeCategories ? (
              <FormField
                label={t(k.scopeCategoriesLabel)}
                name="categoryIds"
                error={errors.scope}
              >
                {categoriesLoading ? (
                  <p className="text-[12.5px] text-[color:var(--text-muted)]">
                    {t(k.scopeLoading)}
                  </p>
                ) : categoryOptions.length === 0 ? (
                  <p className="text-[12.5px] text-[color:var(--text-muted)]">
                    {t(k.scopeCategoriesEmpty)}
                  </p>
                ) : (
                  <MultiSelect
                    options={categoryOptions}
                    value={value.categoryIds}
                    onChange={(ids) => {
                      onChange({ categoryIds: ids });
                      onClearError("scope");
                    }}
                    placeholder={t(k.scopePickerPlaceholder)}
                    searchPlaceholder={t(k.scopeSearchPlaceholder)}
                    noResultsLabel={t(k.scopeNoResults)}
                    hasError={Boolean(errors.scope)}
                    aria-label={t(k.scopeCategoriesLabel)}
                  />
                )}
              </FormField>
            ) : null}
          </div>
        </FormField>
      ) : null}
    </>
  );
}
